import { Component, OnDestroy, OnInit, TemplateRef } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { NgbModal, NgbModalConfig, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { AlertService, Option, SessionService } from "@recore-solutions/nstar-lib";
import { combineLatest, interval, Observable, of, PartialObserver, Subscription } from "rxjs";
import {
	catchError,
	debounce,
	debounceTime,
	delay,
	map,
	shareReplay,
	startWith,
	switchMap,
} from "rxjs/operators";
import { ConfigurationService } from "../configuration.service";
import { PermissionListGuard } from "../permission-list.guard";
import { ListingSummary } from "../shared/interfaces/listing-summary";
import { ListingSummaryService } from "../shared/services/listing-summary.service";
import { ListingService } from "../shared/services/listing.service";
import { LoadingProgressBarService } from "../shared/services/loading-progress-bar.service";
import { SharedService } from "../shared/services/shared.service";
import { HomeListingResultsService } from "./home-listing-results.service";
import { pageSizes } from "./page-sizes";
import { ListingSortColumn } from "./types/ListingSortColumn";
import { UrlService } from "../shared/services/url.service";
import { Store, select } from "@ngrx/store";
import { selectListingServiceOffering } from "../listing-service-offering.selectors";
import { PreferencesService } from "../preferences/preferences.service";
import { INACTIVE_LISTING_NAVIGATION_STATUSES } from "../shared/interfaces/listing-status";

@Component({
	selector: "app-home",
	templateUrl: "./home.component.html",
	styleUrls: ["./home.component.scss"],
})
export class HomeComponent implements OnInit, OnDestroy {
	listingSortColumn: typeof ListingSortColumn = ListingSortColumn;

	canAdd$: Observable<boolean>;
	cardOrRow$: Observable<"card" | "row">;
	showPreviewAd$: Observable<boolean>;
	set cardOrRow(value: "card" | "row") {
		this.homeListingResultsService.cardOrRowSubject.next(value);
	}
	listing$: Observable<ListingSummary[]>;

	// When a request is cancelled and a new one started it leads to rapid changes the loading state.
	// Use debounceTime to smooth that out slightly.
	loading$: Observable<boolean> = this.listingSummaryService.loading$.pipe(debounceTime(10));

	get currentPage(): number {
		return this.homeListingResultsService.currentPage;
	}
	set currentPage(page: number) {
		this.homeListingResultsService.currentPage = page;
	}
	currentPage$: Observable<number>;
	resultCount$: Observable<number>;
	currentRange$: Observable<{ start: number; end: number; total: number }>;

	classLabel$: Observable<string>;
	statusLabel$: Observable<string>;
	whenLabel$: Observable<string>;
	sortLabel$: Observable<string>;

	listingSummaryRequestForm: UntypedFormGroup =
		this.homeListingResultsService.listingSummaryRequestForm;
	listingSummaryRequestFormInvalid$: Observable<boolean>;

	currentListingSummary: ListingSummary;

	sortColumnControl: UntypedFormControl;
	isSortAscControl: UntypedFormControl;

	showWelcome$: Observable<boolean>;

	pageSize$: Observable<Option[]> = of(pageSizes);
	pageSizeControl: UntypedFormControl = this.homeListingResultsService.pageSizeControl;

	private subscriptions: Subscription[] = [];

	get maxSearchResults(): number {
		return this.configService?.config?.listings?.maxSearchResults || 1000;
	}

	constructor(
		private homeListingResultsService: HomeListingResultsService,
		private sharedService: SharedService,
		private loadingProgressBarService: LoadingProgressBarService,
		private modalService: NgbModal,
		private config: NgbModalConfig,
		private listingSummaryService: ListingSummaryService,
		private permissionListGuard: PermissionListGuard,
		private listingService: ListingService,
		private router: Router,
		private alertService: AlertService,
		private configService: ConfigurationService,
		private sessionService: SessionService,
		private urlService: UrlService,
		private store: Store,
		private preferencesService: PreferencesService
	) {
		this.cardOrRow$ = this.homeListingResultsService.cardOrRow$;
		this.config.centered = true;
		this.sortColumnControl = this.listingSummaryRequestForm.get("sortColumn") as UntypedFormControl;
		this.isSortAscControl = this.listingSummaryRequestForm.get("isSortASC") as UntypedFormControl;
		this.listingSummaryRequestFormInvalid$ = this.listingSummaryRequestForm.statusChanges.pipe(
			startWith(this.listingSummaryRequestForm.status),
			map((status) => status === "INVALID"),
			debounce(() => interval(1000))
		);
		this.homeListingResultsService.inProgressFn = () =>
			this.loadingProgressBarService.setProgress(1);
		this.homeListingResultsService.finishedFn = () =>
			this.loadingProgressBarService.clearProgress();
		this.resultCount$ = this.homeListingResultsService.listingSummaryCount$;
		this.currentPage$ = this.homeListingResultsService.currentPage$;
		this.currentRange$ = combineLatest([
			this.currentPage$,
			this.pageSizeControl.valueChanges.pipe(startWith(this.pageSizeControl.value)),
			this.resultCount$,
		]).pipe(
			map(([offset, length, total]) => ({
				start: (offset - 1) * length + 1,
				end: offset * length,
				total,
			}))
		);
		this.canAdd$ = this.permissionListGuard
			.hasPermissionLevelsAndList(null, null, null, ["Agent Subscriber", "Agent Affiliate"])
			.pipe(shareReplay({ bufferSize: 1, refCount: true }));
		this.showWelcome$ = this.homeListingResultsService.searchNumber$.pipe(
			map((num) => num < 2),
			delay(0)
		);

		const showPreviewPreference$: Observable<boolean> = this.preferencesService
			.getPreference(["homepage", "previewAd"])
			.pipe(
				map((value) => {
					let showPreview = true;
					if (value && value === "hide") {
						showPreview = false;
					}
					return showPreview;
				}),
				shareReplay({
					refCount: true,
					bufferSize: 1,
				})
			);

		const adBuilderPermission$: Observable<boolean> = this.store
			.pipe(select(selectListingServiceOffering("AdBuilder")))
			.pipe(map((offering) => (offering ? true : false)));

		const shouldShowCampaigns$: Observable<boolean> = this.configService.config$.pipe(
			map((config) => config.homepage.adbuilderCampaigns)
		);

		const isOriginalUser$: Observable<boolean> = combineLatest([
			this.sessionService.userBasic$,
			this.sessionService.memberBasic$,
		]).pipe(
			map(
				([userBasics, memberBasics]) => userBasics.memberReference === memberBasics.memberReference
			)
		);

		this.showPreviewAd$ = combineLatest([
			shouldShowCampaigns$,
			showPreviewPreference$,
			adBuilderPermission$,
			isOriginalUser$,
		]).pipe(
			map(
				([featureEnabled, preference, permission, isOriginalUser]) =>
					featureEnabled && preference && permission && isOriginalUser
			)
		);

		this.listing$ = this.homeListingResultsService.listingSummary$.pipe(
			switchMap((listingSummary) =>
				this.showPreviewAd$.pipe(
					switchMap((showPreview) =>
						showPreview ? this.findListingCampaigns(listingSummary) : of(listingSummary)
					),
					startWith(listingSummary.map((summary) => ({ ...summary, adbuilderLoaded: false })))
				)
			)
		);
	}

	ngOnInit() {
		this.refreshResults();
	}

	findListingCampaigns(listingSummary: ListingSummary[]): Observable<ListingSummary[]> {
		listingSummary = listingSummary.map((ls) => {
			const canNavigate = this.canNavigateToListing(ls);
			return {
				...ls,
				showPreviewAd: this.listingSummaryService.showAdbuilder(
					ls,
					this.sessionService.userBasics.memberReference
				),
				canNavigate,
			};
		});
		const listingSks = listingSummary
			.filter((summary) =>
				this.listingSummaryService.queryForAdbuilderCampaigns(
					summary,
					this.sessionService.userBasics.memberReference
				)
			)
			.map((summary) => summary.listingSK)
			.join(",");

		if (listingSks) {
			return this.urlService.getAdBuilderCampaigns(listingSks).pipe(
				map((campaigns) => {
					const updatedSummary: ListingSummary[] = listingSummary.map((ls) => {
						const foundCampaign = campaigns.find(
							(campaign) => ls.listingSK.toString() === campaign.ListingKey.toString()
						);
						return {
							...ls,
							adbuilderCampaign: foundCampaign,
							adbuilderLoaded: true,
							showPreviewAd: ls.showPreviewAd || !!foundCampaign,
						};
					});

					return updatedSummary;
				}),
				catchError(() => {
					this.alertService.alertDanger("Unable to get AdBuilder Campaigns");
					return of(listingSummary.map((summary) => ({ ...summary, adbuilderLoaded: true })));
				}),
				shareReplay({
					bufferSize: 1,
					refCount: true,
				})
			);
		} else {
			return of(
				listingSummary.map((summary) => ({
					...summary,
					adbuilderLoaded: true,
				}))
			);
		}
	}

	navigateToListing(listingSummary: ListingSummary) {
		if (!!listingSummary?.listingID && this.canNavigateToListing(listingSummary)) {
			this.router.navigate(["listing", (listingSummary as any).listingID]);
		}
	}

	toggleOption(control: UntypedFormControl, options: Option[], option: Option): void {
		const newValue = control.value || [];
		const index = !!option ? newValue.indexOf(option.value) : -1;
		if (index > -1) {
			control.setValue(newValue.filter((o) => o !== option.value));
		} else if (!!option) {
			control.setValue(newValue.concat(option.value));
		} else {
			control.setValue(newValue.length < options.length ? options.map((o) => o.value) : []);
		}
	}

	pageTo(page: number): void {
		// this.listingSummaryRequestForm.get('offset').setValue(page);
		this.currentPage = page;
	}

	setSortColumn(sortColumn: string): void {
		this.listingSummaryRequestForm.get("sortColumn").setValue(sortColumn);
	}

	openModal(listingSummary: ListingSummary, tempRef: TemplateRef<any>): NgbModalRef {
		this.currentListingSummary = listingSummary;
		return this.modalService.open(tempRef);
	}

	deleteListing(listingSummary: ListingSummary): void {
		this.currentListingSummary = null;
		this.listingService
			.deleteListing(listingSummary)
			.pipe()
			.subscribe(this.listingService.deleteListingObserver(listingSummary));
	}

	recoverListing(listingSummary: ListingSummary): void {
		this.currentListingSummary = null;
		this.listingService
			.recoverListing(listingSummary)
			.pipe(catchError(this.alertError("Error recovering listing #" + listingSummary.listingID)))
			.subscribe(this.listingService.recoverListingObserver(listingSummary));
	}

	copyListing(listingSummary: ListingSummary, tempRef: TemplateRef<any>): void {
		const modal = this.openModal(listingSummary, tempRef);
		const subscriber = this.modalClosingSubscriber(
			modal,
			"Error copying listing #" + listingSummary.listingID
		);
		this.listingService
			.copyListing(listingSummary.listingSK)
			.pipe(map((data) => data.records[0].listingID))
			.subscribe(subscriber);
	}

	cloneListing(listingSummary: ListingSummary, tempRef: TemplateRef<any>): void {
		const modal = this.openModal(listingSummary, tempRef);
		const subscriber = this.modalClosingSubscriber(
			modal,
			"Error cloning listing #" + listingSummary.listingID
		);
		this.listingService
			.cloneListing(listingSummary.listingSK)
			.pipe(map((data) => data.records[0].listingID))
			.subscribe(subscriber);
	}

	modalClosingSubscriber(
		modal: NgbModalRef,
		errorMessage: string = "Error!"
	): PartialObserver<number> {
		return {
			next: this.handleSuccess(modal),
			error: this.handleError(modal, errorMessage),
		};
	}

	handleSuccess = (modal: NgbModalRef) => (listingID: number) => {
		this.closeModalAndClearCurrent(modal)();
		this.sharedService.navigateToEditListing(listingID);
	};

	handleError = (modal: NgbModalRef, errorMessage: string) => (_error: any) => {
		this.alertError(errorMessage)(null);
		this.closeModalAndClearCurrent(modal)();
	};

	alertError = (errorMessage: string) => (_error: any) => {
		this.alertService.alertDanger(errorMessage);
		return of(null);
	};

	closeModalAndClearCurrent = (modal: NgbModalRef) => () => {
		modal.close();
		this.currentListingSummary = null;
	};

	refreshResults = (): void => {
		this.homeListingResultsService.refresh();
	};

	ngOnDestroy(): void {
		this.subscriptions.forEach((s) => s.unsubscribe());
	}

	navigateToAd($event: ListingSummary) {
		const newWindow = window.open("", "_blank");
		if (newWindow) {
			newWindow.focus();
			if ($event?.adbuilderCampaign?.ReviewUrl) {
				newWindow.location.href = $event?.adbuilderCampaign?.ReviewUrl;
			} else {
				this.urlService.getAdbuilderUrl($event.listingSK).subscribe({
					next: (response) => {
						if (response.url) {
							newWindow.location.href = response.url;
						} else {
							newWindow.close();
							this.alertService.alertDanger("Error retrieving AdBuilder URL");
						}
					},
					error: () => {
						newWindow.close();
						this.alertService.alertDanger("Error retrieving AdBuilder URL");
					},
				});
			}
		}
	}

	private canNavigateToListing(listingSummary: ListingSummary): boolean {
		return (
			INACTIVE_LISTING_NAVIGATION_STATUSES.indexOf(listingSummary?.listingStatusCD) === -1 ||
			this.sessionService.permissionLevels.some((pl) =>
				this.configService.config.permissions.navigateToDeleted.includes(
					pl?.accessName?.toLocaleLowerCase()
				)
			)
		);
	}
}
